import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuidePhotoCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Photo sur CV : Obligatoire ou Non en 2025 ? Guide Complet</title>
                <meta name="description" content="Photo sur CV : est-ce obligatoire en France en 2025 ? Législation, avantages/inconvénients, conseils par secteur, alternatives LinkedIn. Guide complet avec exemples." />
                <meta name="keywords" content="photo CV obligatoire, mettre photo CV, CV avec photo, CV sans photo, photo CV France, règles photo CV" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/photo-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment gérer la photo sur son CV",
                  "description": "Décidez si vous devez mettre une photo sur votre CV et comment la choisir selon votre secteur et la législation française.",
                  "step": [
                    {"@type": "HowToStep", "name": "Vérifier la législation", "text": "En France, la photo n'est pas obligatoire. L'employeur ne peut pas l'exiger légalement."},
                    {"@type": "HowToStep", "name": "Évaluer son secteur d'activité", "text": "La photo est souvent attendue dans le commercial, l'hôtellerie ou la communication. Déconseillée dans les secteurs très réglementés."},
                    {"@type": "HowToStep", "name": "Choisir une photo professionnelle", "text": "Fond neutre, tenue adaptée, sourire naturel, format carré ou portrait."},
                    {"@type": "HowToStep", "name": "Respecter les critères techniques", "text": "Résolution suffisante, format JPG ou PNG, taille raisonnable dans le document."},
                    {"@type": "HowToStep", "name": "Positionner la photo correctement", "text": "En haut à droite ou à gauche du CV, jamais en pied de page."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Photo CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">📸</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Photo sur CV : Obligatoire ou Non en 2025 ?
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Tout ce que vous devez savoir sur la photo de CV : législation, avantages, inconvénients et conseils par secteur
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="photo-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">📋 Réponse rapide</h2>
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6">
                        <p className="text-xl text-gray-800 font-semibold mb-4">
                            <strong>Non, la photo n'est pas obligatoire sur un CV en France.</strong> Cependant, elle reste très courante et parfois attendue selon les secteurs.
                        </p>
                        <p className="text-gray-700">
                            La décision dépend de votre secteur d'activité, du type de poste visé et de vos préférences personnelles.
                        </p>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">⚖️ Législation française</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        En France, la photo sur le CV n'est <strong>pas obligatoire</strong> légalement. Cependant, elle est très répandue dans la culture française du recrutement.
                    </p>
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl mt-6">
                        <p className="text-gray-800 font-semibold mb-2">⚠️ Important :</p>
                        <p className="text-gray-700">
                            Les recruteurs n'ont pas le droit de rejeter une candidature uniquement sur la base de l'absence de photo. 
                            Cependant, dans certains secteurs (commerce, hôtellerie, etc.), une photo professionnelle peut être un atout.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">✅ Avantages de mettre une photo</h2>
                        <ul className="space-y-3 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-green-600 mr-3">✓</span>
                                <span>Humanise votre candidature</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-3">✓</span>
                                <span>Attire l'attention du recruteur</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-3">✓</span>
                                <span>Montre votre professionnalisme</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-3">✓</span>
                                <span>Attendu dans certains secteurs</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-green-600 mr-3">✓</span>
                                <span>Facilite la mémorisation</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">❌ Inconvénients de mettre une photo</h2>
                        <ul className="space-y-3 text-gray-700">
                            <li className="flex items-start">
                                <span className="text-red-600 mr-3">✗</span>
                                <span>Risque de discrimination</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-600 mr-3">✗</span>
                                <span>Peut être mal perçue si inappropriée</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-600 mr-3">✗</span>
                                <span>Prend de la place sur le CV</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-600 mr-3">✗</span>
                                <span>Moins adapté aux candidatures internationales</span>
                            </li>
                            <li className="flex items-start">
                                <span className="text-red-600 mr-3">✗</span>
                                <span>Peut révéler l'âge</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">🏢 Par secteur d'activité</h2>
                    <div className="space-y-4">
                        <div className="bg-green-50 rounded-xl p-4">
                            <h3 className="font-bold text-gray-900 mb-2">✅ Photo recommandée :</h3>
                            <p className="text-gray-700">Commerce, vente, hôtellerie-restauration, communication, événementiel, métiers de l'accueil</p>
                        </div>
                        <div className="bg-yellow-50 rounded-xl p-4">
                            <h3 className="font-bold text-gray-900 mb-2">⚠️ Photo optionnelle :</h3>
                            <p className="text-gray-700">Marketing, RH, finance, conseil, management, éducation</p>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4">
                            <h3 className="font-bold text-gray-900 mb-2">❌ Photo généralement évitée :</h3>
                            <p className="text-gray-700">IT/Tech, ingénierie, recherche, juridique, candidatures internationales (UK, USA, Canada)</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">📸 Conseils si vous mettez une photo</h2>
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-6">
                            <h3 className="font-bold text-gray-900 mb-2">1. Photo professionnelle</h3>
                            <p className="text-gray-700">Faites appel à un photographe professionnel ou utilisez un fond neutre avec un bon éclairage</p>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-6">
                            <h3 className="font-bold text-gray-900 mb-2">2. Tenue adaptée</h3>
                            <p className="text-gray-700">Habillez-vous comme pour un entretien d'embauche, de manière professionnelle</p>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-6">
                            <h3 className="font-bold text-gray-900 mb-2">3. Expression naturelle</h3>
                            <p className="text-gray-700">Souriez naturellement, regardez l'objectif, soyez vous-même</p>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-6">
                            <h3 className="font-bold text-gray-900 mb-2">4. Format et taille</h3>
                            <p className="text-gray-700">Format carré ou rectangulaire, taille 4x5 cm environ, bonne résolution (300 dpi minimum)</p>
                        </div>
                        <div className="border-l-4 border-blue-500 pl-6">
                            <h3 className="font-bold text-gray-900 mb-2">5. Mise à jour</h3>
                            <p className="text-gray-700">Utilisez une photo récente (moins de 2 ans) qui vous ressemble vraiment</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">🔗 Alternatives : LinkedIn</h2>
                    <p className="text-gray-700 leading-relaxed mb-4">
                        Si vous ne mettez pas de photo sur votre CV, assurez-vous d'avoir une photo professionnelle sur votre profil LinkedIn. 
                        Les recruteurs consulteront souvent votre profil LinkedIn en complément de votre CV.
                    </p>
                    <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-gray-800 font-semibold">💡 Astuce :</p>
                        <p className="text-gray-700">
                            Même sans photo sur le CV, un profil LinkedIn avec une photo professionnelle permet aux recruteurs de vous visualiser 
                            et peut compenser l'absence de photo sur le CV.
                        </p>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">Votre CV donne-t-il la meilleure première impression ?</h2>
                    <p className="text-xl mb-6">Notre IA analyse la mise en forme, la photo et tous les éléments visuels de votre CV — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-blue-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Analyser mon CV gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuidePhotoCV;

